import React, { useState } from 'react';
import { Plus, Info, Trash2, X, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useAuth } from '@clerk/clerk-react';
import { useNotesStore, Note } from '../../stores/notesStore';
import { toast } from 'sonner';
import { useI18n, useLocaleFormat } from '../../i18n';

interface NotesContextMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotesContextMenu({ isOpen, onClose }: NotesContextMenuProps) {
  const { t, isRTL } = useI18n();
  const fmt = useLocaleFormat();
  const [swipedItem, setSwipedItem] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  
  const { getToken } = useAuth();
  const { notes, deleteNote } = useNotesStore();
  
  // Safe array
  const safeNotes = Array.isArray(notes) ? notes : [];
  
  // Format relative time
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return t('notes.relativeTime.justNow');
    if (diffMins < 60) return t('notes.relativeTime.minutes', { count: diffMins });
    if (diffHours < 24)
      return t(diffHours > 1 ? 'notes.relativeTime.hoursPlural' : 'notes.relativeTime.hours', { count: diffHours });
    if (diffDays < 7)
      return t(diffDays > 1 ? 'notes.relativeTime.daysPlural' : 'notes.relativeTime.days', { count: diffDays });
    return fmt.date(date);
  };

  const handleSwipe = (itemId: string, offset: number) => {
    if (isRTL ? offset > 50 : offset < -50) {
      setSwipedItem(itemId);
    } else {
      setSwipedItem(null);
    }
  };

  const handleInfo = (item: Note) => {
    toast.info(
      `${t('notes.info.from', { title: item.document_title || t('notes.info.unknownDocument') })}\n` +
      `${t('notes.info.type')}: ${item.note_type || t('notes.title')}\n` +
      `${t('notes.info.page')}: ${item.page_number || t('common.notAvailable')}\n` +
      `${t('notes.info.created')}: ${fmt.dateTime(item.created_at)}`
    );
    setSwipedItem(null);
  };

  const handleDelete = async (item: Note) => {
    if (!confirm(t('notes.confirmDeleteShort'))) {
      setSwipedItem(null);
      return;
    }
    
    try {
      setDeletingId(item.id);
      const token = await getToken();
      if (!token) {
        toast.error(t('library.toast.signIn'));
        return;
      }
      await deleteNote(item.id, token);
      toast.success(t('notes.toast.deleted'));
      setSwipedItem(null);
    } catch (error) {
      toast.error(t('notes.toast.deleteFailed'));
      console.error('Delete error:', error);
    } finally {
      setDeletingId(null);
    }
  };
  
  const handleCreateNote = () => {
    toast.info(t('notes.toast.createHint'));
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Dimming Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/40 z-50 md:hidden"
            onClick={onClose}
          />

          {/* Context Menu */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed start-4 end-4 top-1/2 -translate-y-1/2 z-50 md:hidden max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200/50">
                <h2 className="text-xl font-semibold text-[#434343]">{t('notes.title')}</h2>
                <button
                  aria-label={t("common.close")}
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-200/60 hover:bg-gray-300/60 active:scale-95 transition-all"
                >
                  <X className="h-5 w-5 text-[#434343]" />
                </button>
              </div>

              {/* Create New Button */}
              <div className="px-4 pt-4">
                <button 
                  onClick={handleCreateNote}
                  className="flex w-full items-center gap-3 px-4 py-4 rounded-2xl bg-gradient-to-br from-[#7367FF]/10 to-[#2DD2FE]/10 border border-[#7367FF]/20 hover:from-[#7367FF]/20 hover:to-[#2DD2FE]/20 active:scale-[0.98] transition-all duration-200"
                >
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7367FF] to-[#2DD2FE] flex items-center justify-center shadow-lg shadow-[#7367FF]/30">
                    <Plus className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-[15px] font-medium text-[#7367FF]">{t('notes.createNew')}</span>
                </button>
              </div>

              {/* Note Items List */}
              <div className="overflow-y-auto flex-1 px-4 py-4">
                <div className="space-y-2">
                  {safeNotes.length === 0 ? (
                    <p className="text-center text-[#434343]/50 py-8">
                      {t('notes.emptyShort')}
                    </p>
                  ) : (
                    safeNotes.map((item) => (
                    <SwipeableRow
                      key={item.id}
                      item={item}
                      isOpen={swipedItem === item.id}
                        isDeleting={deletingId === item.id}
                      onSwipe={(offset) => handleSwipe(item.id, offset)}
                      onInfo={() => handleInfo(item)}
                      onDelete={() => handleDelete(item)}
                        formatTime={formatRelativeTime}
                        isRTL={isRTL}
                        fallbackTitle={t('notes.title')}
                    />
                    ))
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

interface SwipeableRowProps {
  item: Note;
  isOpen: boolean;
  isDeleting?: boolean;
  onSwipe: (offset: number) => void;
  onInfo: () => void;
  onDelete: () => void;
  formatTime: (date: string) => string;
  isRTL: boolean;
  fallbackTitle: string;
}

function SwipeableRow({ item, isOpen, isDeleting, onSwipe, onInfo, onDelete, formatTime, isRTL, fallbackTitle }: SwipeableRowProps) {
  const { t } = useI18n();
  const [dragX, setDragX] = useState(0);
  
  // Get display text
  const title = item.selected_text?.slice(0, 50) || item.note_content?.slice(0, 50) || fallbackTitle;
  const preview = item.note_content?.slice(0, 80) || item.selected_text?.slice(0, 80) || '';

  return (
    <div dir={isRTL ? "rtl" : "ltr"} className="relative overflow-hidden rounded-2xl bg-white border border-gray-200/50 shadow-sm">
      {/* Action Buttons (Hidden Behind) */}
      <div className="absolute end-0 top-0 bottom-0 flex">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onInfo();
          }}
          aria-label={t("common.info")}
          className="w-[74px] bg-[#aeaeb2] flex items-center justify-center active:bg-[#9e9ea2] transition-colors"
        >
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <Info className="h-5 w-5 text-white" />
          </div>
        </button>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={t("common.delete")}
          disabled={isDeleting}
          className="w-[74px] bg-[#ff3b30] flex items-center justify-center active:bg-[#e53429] transition-colors disabled:opacity-50"
        >
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            {isDeleting ? (
              <Loader2 className="h-5 w-5 text-white animate-spin" />
            ) : (
            <Trash2 className="h-5 w-5 text-white" />
            )}
          </div>
        </button>
      </div>

      {/* Swipeable Content */}
      <motion.div
        drag="x"
        // Actions sit on the trailing edge, so the swipe runs the other way in RTL.
        dragConstraints={isRTL ? { left: 0, right: 148 } : { left: -148, right: 0 }}
        dragElastic={0.1}
        onDragEnd={(event, info) => {
          const offset = info.offset.x;
          onSwipe(offset);

          if (isRTL ? offset < 50 : offset > -50) {
            setDragX(0);
          } else {
            setDragX(isRTL ? 148 : -148);
          }
        }}
        animate={{ x: dragX }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className="relative bg-white rounded-2xl cursor-grab active:cursor-grabbing"
        style={{ touchAction: 'pan-y' }}
      >
        <div className="flex items-center px-4 py-4">
          <div className="flex-1 min-w-0">
            <p dir="auto" className="text-[15px] font-medium text-[#434343] truncate">
              {title}{title.length >= 50 ? '...' : ''}
            </p>
            {preview && preview !== title && (
            <p dir="auto" className="text-[13px] text-[#434343]/60 mt-0.5 truncate">
                {preview}{preview.length >= 80 ? '...' : ''}
            </p>
            )}
            <p className="text-[11px] text-[#434343]/40 mt-0.5">
              {item.document_title ? `${item.document_title} • ` : ''}{formatTime(item.updated_at || item.created_at)}
            </p>
          </div>
          <div className="ms-4 flex-shrink-0">
            <svg
              className="w-2 h-4 text-[#434343]/30"
              fill="currentColor"
              viewBox="0 0 8 13"
            >
              <path d="M1.5 0.5L7 6.5L1.5 12.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
